import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import {
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Box,
  Button,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { enqueueSnackbar } from "notistack";
import VerificationInput from "./VerificationPinInput";
import {
  reset_password,
  verify_code,
  forgot_password_api,
} from "../../DAL/auth/auth";
import { getPasswordStrength } from "../../components/menu/PasswirdStrength";

// Your steps
const steps = ["Forgot Password", "Verify Code", "Reset Password"];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // States for forms
  const [forgotStates, setForgotStates] = useState({
    email: "",
    enter_new_password: "",
    confirm_new_password: "",
  });
  const [otp, setOtp] = useState("");

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [showRequirements, setShowRequirements] = useState(true);

  const isMobile = useMediaQuery("(max-width:600px)");

  // Handlers
  const handleChange = (e) => {
    setForgotStates({ ...forgotStates, [e.target.name]: e.target.value });
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    const response = await forgot_password_api({
      email: forgotStates.email,
      type: 1,
    });
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setActiveStep(1); // move to OTP step
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setLoading(false);
  };

  const handleOtpComplete = (val) => {
    setOtp(val);
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      enqueueSnackbar("Please enter a valid 6-digit code", {
        variant: "error",
      });
      return;
    }
    const postData = {
      email: forgotStates.email,
      verification_code: otp,
      type: 1,
    };

    setLoading(true);
    const resp = await verify_code(postData);
    if (resp.code === 200) {
      setActiveStep(2);
      enqueueSnackbar(resp.message, { variant: "success" });
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setLoading(false);
    return;
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push("Minimum length is 8 characters");
    }
    if (password.length > 128) {
      errors.push("Maximum length is 128 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter (A-Z)");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter (a-z)");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number (0-9)");
    }
    if (!/[@$!%*?&]/.test(password)) {
      errors.push("At least one special character (@$!%*?&)");
    }

    return errors;
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();

    // Check matching
    if (forgotStates.enter_new_password !== forgotStates.confirm_new_password) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      return;
    }

    // Validate requirements
    const errors = validatePassword(forgotStates.enter_new_password);
    if (errors.length > 0) {
      enqueueSnackbar(`Invalid password: ${errors[0]}`, { variant: "error" });
      return;
    }

    // Submit to API
    const postData = {
      email: forgotStates.email,
      password: forgotStates.enter_new_password,
      confirm_password: forgotStates.confirm_new_password,
      type: 1,
    };

    setLoading(true);
    const resp = await reset_password(postData);
    if (resp.code === 200) {
      navigate("/login");
      enqueueSnackbar("Password reset successfully!", { variant: "success" });
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setLoading(false);
  };

  // TODO: call API here

  return (
    <div className="container h-100 forgot-password-page position-relative">
      {/* Back Button */}
      <div className="row justify-content-center">
        <div className="back-to-login">
          <Link className="text-white" to={"/login"}>
            <ArrowBack
              sx={{
                marginRight: "5px",
                color: "white",
                marginLeft: isMobile ? "17px" : "",
              }}
              className="arrow-mui"
            />
            Back To Login? Login
          </Link>
        </div>

        {/* Stepper */}
        {/* <Box sx={{ width: "100%", mt: 4, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box> */}

        {/* Step 1: Email */}
        {activeStep === 0 && (
          <div className="col-12 col-md-6 col-xl-4 login-box email-box">
            <h2 className="login-title">Forgot Password?</h2>
            <p className="login-subtitle">
              Enter your email and we’ll help you reset your password.
            </p>
            <form onSubmit={handleSubmitEmail} className="forgot-password-form">
              <div className="">
                <label className="mb-1">Enter your email*</label>
                <input
                  required
                  type="email"
                  placeholder="Email*"
                  className="input-field email-input"
                  name="email"
                  value={forgotStates.email}
                  onChange={handleChange}
                />
              </div>
              <button type="submit" className="large-button-contained mt-3">
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: OTP */}
        {activeStep === 1 && (
          <div className="col-12 col-md-6 col-xl-4 login-box otp-box">
            <h2 className="login-title">Password Reset</h2>
            <p className="login-subtitle">
              We sent a 6-digit password code to your email. Enter the code to
              proceed.
            </p>
            <form className="otp-form" onSubmit={handleSubmitOtp}>
              <VerificationInput onInputFilled={handleOtpComplete} />
              <button type="submit" className="large-button-contained mt-3">
                {loading ? "Continuing..." : "Continue"}
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Reset Password */}
        {activeStep === 2 && (
          <div className="col-12 col-md-6 col-xl-4 login-box password-box">
            <h2 className="login-title">Create a New Password</h2>
            <form
              onSubmit={handleSubmitPassword}
              className="reset-password-form"
            >
              {/* New Password */}
              <div className="position-relative">
                <label className="mb-1">New Password*</label>
                <input
                  required
                  style={{ borderRadius: "5px" }}
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Enter new password*"
                  className="input-field new-password"
                  name="enter_new_password"
                  onChange={handleChange}
                  value={forgotStates.enter_new_password}
                />
                <span
                  className="eye-icon position-absolute"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  style={{ right: "18px", top: "30px", cursor: "pointer" }}
                >
                  {passwordVisible ? (
                    <VisibilityOutlinedIcon className="eye-icon" />
                  ) : (
                    <VisibilityOffOutlinedIcon className="eye-icon" />
                  )}
                </span>

                {/* Strength Indicator */}
                {forgotStates.enter_new_password &&
                  (() => {
                    const { level, color, percentage } = getPasswordStrength(
                      forgotStates.enter_new_password
                    );
                    return (
                      <div className="w-100 mt-2">
                        <div
                          style={{
                            height: 8,
                            borderRadius: 4,
                            background: "#eee",
                            width: "100%",
                            overflow: "hidden",
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${percentage}%`,
                              background: color,
                              transition: "width 0.3s, background 0.3s",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: color,
                              minWidth: 120,
                              maxWidth: 120,
                              textTransform: "capitalize",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {level}
                            <span
                              style={{
                                marginLeft: 6,
                                cursor: "pointer",
                                verticalAlign: "middle",
                                color: "#1976d2",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                              onClick={() =>
                                setShowRequirements((prev) => !prev)
                              }
                              title="Password requirements"
                            >
                              <InfoIcon
                                style={{ fontSize: 16, color: "#1976d2" }}
                              />
                            </span>
                          </span>
                        </div>

                        {/* Requirements List */}
                        {showRequirements && (
                          <div
                            style={{
                              background: "#f8f9fa",
                              borderRadius: 6,
                              padding: "12px 16px",
                              marginTop: 8,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                          >
                            <strong style={{ fontSize: 14 }}>
                              Password Requirements
                            </strong>
                            <ul
                              style={{
                                fontSize: 13,
                                marginBottom: 0,
                                marginTop: 6,
                              }}
                            >
                              <li>Minimum 8 characters</li>
                              <li>Maximum 128 characters</li>
                              <li>One uppercase letter (A-Z)</li>
                              <li>One lowercase letter (a-z)</li>
                              <li>One number (0-9)</li>
                              <li>One special character (@$!%*?&)</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>

              {/* Confirm Password */}
              <div className="position-relative mt-3">
                <label className="mb-1">Confirm New Password*</label>
                <input
                  required
                  style={{ borderRadius: "5px" }}
                  type={confirmPasswordVisible ? "text" : "password"}
                  placeholder="Confirm new password*"
                  className="input-field confirm-password"
                  name="confirm_new_password"
                  value={forgotStates.confirm_new_password}
                  onChange={handleChange}
                />
                <span
                  className="eye-icon position-absolute"
                  onClick={() =>
                    setConfirmPasswordVisible(!confirmPasswordVisible)
                  }
                  style={{ right: "18px", top: "30px", cursor: "pointer" }}
                >
                  {confirmPasswordVisible ? (
                    <VisibilityOutlinedIcon className="eye-icon" />
                  ) : (
                    <VisibilityOffOutlinedIcon className="eye-icon" />
                  )}
                </span>
              </div>

              {/* Submit */}
              <button type="submit" className="large-button-contained mt-3">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
